import { useEffect, useState } from "react";
import { useLogin } from "@refinedev/core";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  clearLoginAttempt,
  hasPendingLoginAttempt,
  resendLoginCode,
  sendLoginCode,
} from "@/lib/supertokens";
import type { LoginVariables } from "@/authProvider";

/**
 * Passwordless email-OTP sign-in — the same flow as the web app, because the
 * panel signs in an ordinary `users` row (one whose `role` is `admin`) rather
 * than any panel-specific account.
 *
 * Two steps in one page: send the code, then exchange it. SuperTokens keeps the
 * in-flight attempt in its own storage, so a reload mid-flow lands back on the
 * code step instead of silently losing it.
 */
type Step = "email" | "code";

export const LoginPage = () => {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { mutate: login, isPending: verifying } = useLogin<LoginVariables>();

  useEffect(() => {
    let cancelled = false;
    void hasPendingLoginAttempt().then((pending) => {
      if (pending && !cancelled) {
        setStep("code");
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSendCode = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setSending(true);
    try {
      await sendLoginCode(email.trim());
      setStep("code");
      setNotice(`We emailed a code to ${email.trim()}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send the code.");
    } finally {
      setSending(false);
    }
  };

  const handleVerify = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    login(
      { email: email.trim(), code: code.trim() },
      {
        onSuccess: (data) => {
          if (!data.success) {
            setError(data.error?.message ?? "Sign-in failed.");
            setCode("");
          }
        },
        onError: (err) => {
          setError(err?.message ?? "Sign-in failed.");
          setCode("");
        },
      }
    );
  };

  const handleResend = async () => {
    setError(null);
    setNotice(null);
    try {
      await resendLoginCode();
      setNotice("Sent a new code.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not resend.");
      await startOver();
    }
  };

  const startOver = async () => {
    await clearLoginAttempt();
    setCode("");
    setStep("email");
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 420 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ mb: 0.5 }}>
            Immersive Admin
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {step === "email"
              ? "Sign in with your admin account's email."
              : "Enter the 6-digit code we emailed you."}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {notice && !error && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {notice}
            </Alert>
          )}

          {step === "email" ? (
            <form onSubmit={handleSendCode}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  required
                  fullWidth
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={sending || email.trim().length === 0}
                >
                  {sending ? "Sending…" : "Send code"}
                </Button>
              </Stack>
            </form>
          ) : (
            <form onSubmit={handleVerify}>
              <Stack spacing={2}>
                <TextField
                  label="Code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  required
                  fullWidth
                  slotProps={{
                    htmlInput: {
                      inputMode: "numeric",
                      autoComplete: "one-time-code",
                      maxLength: 6,
                    },
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={verifying || code.trim().length === 0}
                >
                  {verifying ? "Signing in…" : "Sign in"}
                </Button>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={handleResend}
                  >
                    Resend code
                  </Link>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => void startOver()}
                  >
                    Use a different email
                  </Link>
                </Stack>
              </Stack>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
